import React from "react";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import Badge from "react-bootstrap/Badge";
import { CgWebsite } from "react-icons/cg";
import { BsGithub, BsStar, BsGit, BsCalendar3 } from "react-icons/bs";
import { AiOutlineEye, AiOutlineLink } from "react-icons/ai";

function EnhancedProjectCards(props) {
  // Define tech stack colors for badges
  const techColors = {
    'Python': 'success',
    'JavaScript': 'warning',
    'Java': 'danger',
    'C++': 'info',
    'SQL': 'primary',
    'React': 'info',
    'PyTorch': 'dark',
    'TensorFlow': 'secondary',
    'NLP': 'purple',
    'ML': 'success',
    'AI': 'danger',
    'Deep Learning': 'dark',
    'Computer Vision': 'primary',
    'Streamlit': 'info',
    'Firebase': 'warning',
    'Android': 'success'
  };

  // Get tech stack based on project name and description
  const getTechStack = (title, description) => {
    const techStack = [];
    
    // Add language
    if (props.language) {
      techStack.push(props.language);
    }
    
    // Add technologies based on project content
    if (description.toLowerCase().includes('nlp') || description.toLowerCase().includes('natural language')) {
      techStack.push('NLP');
    }
    if (description.toLowerCase().includes('machine learning') || description.toLowerCase().includes('ml')) {
      techStack.push('ML');
    }
    if (description.toLowerCase().includes('deep learning')) {
      techStack.push('Deep Learning');
    }
    if (description.toLowerCase().includes('llm') || description.toLowerCase().includes('large language')) {
      techStack.push('AI');
    }
    if (description.toLowerCase().includes('streamlit')) {
      techStack.push('Streamlit');
    }
    if (description.toLowerCase().includes('firebase')) {
      techStack.push('Firebase');
    }
    if (description.toLowerCase().includes('android')) {
      techStack.push('Android');
    }
    if (description.toLowerCase().includes('sql')) {
      techStack.push('SQL');
    }
    
    return techStack;
  };

  const techStack = getTechStack(props.title, props.description);

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short' 
    });
  };

  return (
    <Card className="project-card-view enhanced-card">
      <Card.Body style={{ padding: "1.5rem" }}>
        {/* Project Header */}
        <div style={{ marginBottom: "1rem" }}>
          <Card.Title style={{ 
            fontSize: "1.4rem", 
            fontWeight: "bold", 
            marginBottom: "0.5rem",
            color: "#ffffff"
          }}>
            {props.title}
          </Card.Title>
          
          {/* GitHub Stats Row */}
          <div style={{ 
            display: "flex", 
            alignItems: "center", 
            gap: "15px",
            marginBottom: "1rem",
            fontSize: "0.85em",
            color: "#cccccc"
          }}>
            {props.language && (
              <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <BsGit /> {props.language}
              </span>
            )}
            {props.stars > 0 && (
              <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <BsStar /> {props.stars}
              </span>
            )}
            {props.forks > 0 && (
              <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <AiOutlineEye /> {props.forks}
              </span>
            )}
            {props.updated && (
              <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <BsCalendar3 /> {formatDate(props.updated)}
              </span>
            )}
          </div>
        </div>

        {/* Project Description */}
        <Card.Text style={{ 
          textAlign: "justify", 
          marginBottom: "1.5rem", 
          lineHeight: "1.6",
          color: "#e0e0e0"
        }}>
          {props.description}
        </Card.Text>

        {/* Tech Stack Badges */}
        {techStack.length > 0 && (
          <div style={{ 
            marginBottom: "1.5rem",
            display: "flex",
            flexWrap: "wrap",
            gap: "8px"
          }}>
            {techStack.map((tech, index) => (
              <Badge 
                key={index}
                bg={techColors[tech] || 'secondary'}
                style={{ fontSize: "0.75rem", padding: "6px 10px" }}
              >
                {tech}
              </Badge>
            ))}
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ 
          display: "flex", 
          gap: "10px", 
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center"
        }}>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <Button 
              variant="primary" 
              href={props.ghLink} 
              target="_blank"
              size="sm"
            >
              <BsGithub /> &nbsp;
              {props.isBlog ? "Blog" : "Code"}
            </Button>

            {!props.isBlog && props.demoLink && (
              <Button
                variant="outline-primary"
                href={props.demoLink}
                target="_blank"
                size="sm"
              >
                <CgWebsite /> &nbsp;
                Live Demo
              </Button>
            )}
          </div>

          {/* Project Type Badge */}
          <Badge 
            bg="warning" 
            text="dark"
            style={{ fontSize: "0.7rem", padding: "4px 8px" }}
          >
            {props.language === 'Python' ? 'ML/AI Project' : 
             props.language === 'JavaScript' ? 'Web App' : 
             props.language === 'Java' ? 'Android App' : 'Project'}
          </Badge>
        </div>
      </Card.Body>
    </Card>
  );
}

export default EnhancedProjectCards; 